/**
 * Node Connection Rules System
 * Scratch-szerű szabályrendszer: meghatározza, hogy melyik node után melyik node következhet
 */

import { FlowNodeType } from './flowBuilder';

// ============================================
// NODE SZEREPKÖRÖK (Node Roles)
// ============================================

export type NodeRole = 
  | 'trigger'       // Flow indító node-ok (csak a flow elején)
  | 'action'        // Műveletet végző node-ok
  | 'logic'         // Logikai döntést hozó node-ok (if, switch, loop)
  | 'data'          // Adatfeldolgozó node-ok
  | 'communication' // Kommunikációs node-ok (email, sms, notification)
  | 'integration'   // Külső rendszer integrációk
  | 'terminal';     // Flow befejező node-ok (csak a flow végén)

// ============================================
// KIMENET TÍPUSOK (Output Types)
// ============================================

export type OutputType =
  | 'any'          // Bármilyen adat
  | 'boolean'      // Logikai érték (true/false)
  | 'string'       // Szöveg
  | 'number'       // Szám
  | 'object'       // Objektum (1 elem)
  | 'array'        // Tömb (több elem)
  | 'file'         // Fájl
  | 'email'        // Email adat
  | 'customer'     // CRM ügyfél adat (1 customer)
  | 'customers'    // CRM ügyfelek (több customer)
  | 'document'     // Dokumentum adat (1 document)
  | 'documents'    // Dokumentumok (több document)
  | 'success'      // Sikeres végrehajtás
  | 'error'        // Hiba
  | 'multiple';    // Többszörös kimenet (loop, split)

// ============================================
// BEMENET TÍPUSOK (Input Types)
// ============================================

export type InputType = OutputType;

// ============================================
// NODE METADATA
// ============================================

export interface NodeMetadata {
  nodeType: FlowNodeType;
  role: NodeRole;
  
  // Kapcsolódási szabályok
  canBeFirst: boolean;        // Lehet-e flow első node-ja
  canBeLast: boolean;         // Lehet-e flow utolsó node-ja
  requiresPrevious: boolean;  // Kell-e előző node
  
  // Input/Output típusok
  acceptedInputs: InputType[];   // Milyen típusú bemeneteket fogad el
  providedOutputs: OutputType[]; // Milyen típusú kimeneteket ad
  
  // Kapcsolódási korlátozások
  maxInputConnections: number;   // Max hány bemeneti kapcsolat
  maxOutputConnections: number;  // Max hány kimeneti kapcsolat
  
  // Speciális szabályok
  canConnectToSelf: boolean;     // Kapcsolódhat-e önmagához (loop)
  requiresMultipleOutputs: boolean; // Kötelező-e több kimenet (condition, switch)
}

// ============================================
// VALIDÁCIÓS FÜGGVÉNYEK
// ============================================

/**
 * Ellenőrzi, hogy két node összekapcsolható-e
 */
export function canConnect(
  sourceNodeType: FlowNodeType,
  targetNodeType: FlowNodeType,
  sourceOutputType?: OutputType,
  targetInputType?: InputType
): { canConnect: boolean; reason?: string } {
  const sourceMetadata = NODE_CONNECTION_RULES[sourceNodeType];
  const targetMetadata = NODE_CONNECTION_RULES[targetNodeType];
  
  if (!sourceMetadata || !targetMetadata) {
    return { canConnect: false, reason: 'Ismeretlen node típus' };
  }
  
  // Trigger node-ok nem követhetnek más node-okat
  if (targetMetadata.role === 'trigger' && targetMetadata.requiresPrevious === false) {
    return { canConnect: false, reason: 'Trigger node csak a flow elején lehet' };
  }
  
  // Ellenőrizzük a max output kapcsolatokat
  // (ezt a tényleges implementációban a meglévő kapcsolatok számával kell ellenőrizni)
  
  // Ellenőrizzük az input/output típus kompatibilitást
  if (sourceOutputType && targetInputType) {
    const isCompatible = isTypeCompatible(sourceOutputType, targetInputType);
    if (!isCompatible) {
      return { 
        canConnect: false, 
        reason: `Inkompatibilis típusok: ${sourceOutputType} → ${targetInputType}` 
      };
    }
  }
  
  return { canConnect: true };
}

/**
 * Ellenőrzi, hogy két típus kompatibilis-e
 */
export function isTypeCompatible(outputType: OutputType, inputType: InputType): boolean {
  // 'any' mindent elfogad és mindenhez csatlakozhat
  if (inputType === 'any' || outputType === 'any') {
    return true;
  }
  
  // Pontos egyezés
  if (outputType === inputType) {
    return true;
  }
  
  // Speciális kompatibilitások
  const compatibilityMap: Record<OutputType, InputType[]> = {
    'any': ['any', 'boolean', 'string', 'number', 'object', 'array', 'file', 'email', 'customer', 'customers', 'document', 'documents', 'success', 'error', 'multiple'],
    'boolean': ['any', 'boolean', 'string'],
    'string': ['any', 'string', 'object'],
    'number': ['any', 'number', 'string'],
    'object': ['any', 'object', 'string'],
    'array': ['any', 'array', 'object'],
    'file': ['any', 'file', 'string'],
    'email': ['any', 'email', 'object', 'string'],
    'customer': ['any', 'customer', 'object'],
    'customers': ['any', 'customers', 'array', 'object'],
    'document': ['any', 'document', 'object'],
    'documents': ['any', 'documents', 'array', 'object'],
    'success': ['any', 'success', 'boolean', 'string'],
    'error': ['any', 'error', 'object', 'string'],
    'multiple': ['any', 'multiple', 'array']
  };
  
  const compatibleTypes = compatibilityMap[outputType] || [];
  return compatibleTypes.includes(inputType);
}

/**
 * Ellenőrzi, hogy egy node lehet-e a flow első node-ja
 */
export function canBeFirstNode(nodeType: FlowNodeType): boolean {
  const metadata = NODE_CONNECTION_RULES[nodeType];
  return metadata?.canBeFirst || false;
}

/**
 * Ellenőrzi, hogy egy node lehet-e a flow utolsó node-ja
 */
export function canBeLastNode(nodeType: FlowNodeType): boolean {
  const metadata = NODE_CONNECTION_RULES[nodeType];
  return metadata?.canBeLast || false;
}

/**
 * Visszaadja egy node metadata-ját
 */
export function getNodeMetadata(nodeType: FlowNodeType): NodeMetadata | undefined {
  return NODE_CONNECTION_RULES[nodeType];
}

// ============================================
// NODE CONNECTION RULES REGISTRY
// ============================================

export const NODE_CONNECTION_RULES: Partial<Record<FlowNodeType, NodeMetadata>> = {
  // ==========================================
  // TRIGGER NODE-OK
  // ==========================================
  
  'trigger': {
    nodeType: 'trigger',
    role: 'trigger',
    canBeFirst: true,
    canBeLast: false,
    requiresPrevious: false,
    acceptedInputs: [],
    providedOutputs: ['any'],
    maxInputConnections: 0,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'webhook_trigger': {
    nodeType: 'webhook_trigger',
    role: 'trigger',
    canBeFirst: true,
    canBeLast: false,
    requiresPrevious: false,
    acceptedInputs: [],
    providedOutputs: ['object', 'any'],
    maxInputConnections: 0,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'manual_trigger': {
    nodeType: 'manual_trigger',
    role: 'trigger',
    canBeFirst: true,
    canBeLast: false,
    requiresPrevious: false,
    acceptedInputs: [],
    providedOutputs: ['any'],
    maxInputConnections: 0,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'schedule_trigger': {
    nodeType: 'schedule_trigger',
    role: 'trigger',
    canBeFirst: true,
    canBeLast: false,
    requiresPrevious: false,
    acceptedInputs: [],
    providedOutputs: ['object'],
    maxInputConnections: 0,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'form_trigger': {
    nodeType: 'form_trigger',
    role: 'trigger',
    canBeFirst: true,
    canBeLast: false,
    requiresPrevious: false,
    acceptedInputs: [],
    providedOutputs: ['object'],
    maxInputConnections: 0,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'email_trigger': {
    nodeType: 'email_trigger',
    role: 'trigger',
    canBeFirst: true,
    canBeLast: false,
    requiresPrevious: false,
    acceptedInputs: [],
    providedOutputs: ['email', 'object'],
    maxInputConnections: 0,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'file_trigger': {
    nodeType: 'file_trigger',
    role: 'trigger',
    canBeFirst: true,
    canBeLast: false,
    requiresPrevious: false,
    acceptedInputs: [],
    providedOutputs: ['file', 'object'],
    maxInputConnections: 0,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'telegram_trigger': {
    nodeType: 'telegram_trigger',
    role: 'trigger',
    canBeFirst: true,
    canBeLast: false,
    requiresPrevious: false,
    acceptedInputs: [],
    providedOutputs: ['object', 'string'],
    maxInputConnections: 0,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'database_trigger': {
    nodeType: 'database_trigger',
    role: 'trigger',
    canBeFirst: true,
    canBeLast: false,
    requiresPrevious: false,
    acceptedInputs: [],
    providedOutputs: ['object', 'array'],
    maxInputConnections: 0,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // ==========================================
  // LOGIC NODE-OK
  // ==========================================
  
  'condition': {
    nodeType: 'condition',
    role: 'logic',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['boolean', 'any'],
    maxInputConnections: 1,
    maxOutputConnections: 2, // true és false ág
    canConnectToSelf: false,
    requiresMultipleOutputs: true
  },
  
  'loop': {
    nodeType: 'loop',
    role: 'logic',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['array', 'object', 'customers', 'documents'],
    providedOutputs: ['multiple', 'any'],
    maxInputConnections: 1,
    maxOutputConnections: 2, // Loop body és exit
    canConnectToSelf: true,
    requiresMultipleOutputs: false
  },
  
  'switch': {
    nodeType: 'switch',
    role: 'logic',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['any'],
    maxInputConnections: 1,
    maxOutputConnections: 10, // Több ág
    canConnectToSelf: false,
    requiresMultipleOutputs: true
  },
  
  'merge': {
    nodeType: 'merge',
    role: 'logic',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['any'],
    maxInputConnections: 10, // Több bemenet
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'wait': {
    nodeType: 'wait',
    role: 'logic',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['any'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // ==========================================
  // CRM NODE-OK (PÉLDA: 1 vs több customer)
  // ==========================================
  
  'crm_query': {
    nodeType: 'crm_query',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['customers', 'array'], // TÖBB customer
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'crm_action': {
    nodeType: 'crm_action',
    role: 'integration',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['customer', 'object'], // 1 customer
    providedOutputs: ['customer', 'success'], // 1 customer vagy success
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'get_contact_persons': {
    nodeType: 'get_contact_persons',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['customer', 'string'], // 1 customer ID vagy customer object
    providedOutputs: ['array'], // Több contact person
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'missing_items_query': {
    nodeType: 'missing_items_query',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['object'],
    providedOutputs: ['array'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // ==========================================
  // COMMUNICATION NODE-OK
  // ==========================================
  
  'smtp_email': {
    nodeType: 'smtp_email',
    role: 'communication',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['email', 'object', 'string'],
    providedOutputs: ['success', 'error'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'notification': {
    nodeType: 'notification',
    role: 'communication',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['any', 'string', 'object'],
    providedOutputs: ['success', 'error'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'telegram_message': {
    nodeType: 'telegram_message',
    role: 'communication',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['success', 'error'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'sms_send': {
    nodeType: 'sms_send',
    role: 'communication',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['success', 'error'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'seeme_sms_send': {
    nodeType: 'seeme_sms_send',
    role: 'communication',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['success', 'error'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'flintsign_sign': {
    nodeType: 'flintsign_sign',
    role: 'integration',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['document', 'file', 'object'],
    providedOutputs: ['document', 'success'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // ==========================================
  // DATA PROCESSING NODE-OK
  // ==========================================
  
  'json_processor': {
    nodeType: 'json_processor',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['object', 'string'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'data_transform': {
    nodeType: 'data_transform',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['any'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // ==========================================
  // TERMINAL NODE-OK
  // ==========================================
  
  'output_display': {
    nodeType: 'output_display',
    role: 'terminal',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: [],
    maxInputConnections: 1,
    maxOutputConnections: 0,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'debug_logger': {
    nodeType: 'debug_logger',
    role: 'terminal',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['any'], // Pass-through is lehetséges
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // ==========================================
  // AI NODE-OK
  // ==========================================
  
  'openai_chat': {
    nodeType: 'openai_chat',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['string', 'object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'claude_chat': {
    nodeType: 'claude_chat',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['string', 'object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'ai_text_generator': {
    nodeType: 'ai_text_generator',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['string'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'ai_translator': {
    nodeType: 'ai_translator',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string'],
    providedOutputs: ['string'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // ==========================================
  // FILE PROCESSING NODE-OK
  // ==========================================
  
  'file_read': {
    nodeType: 'file_read',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['file', 'string'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'file_write': {
    nodeType: 'file_write',
    role: 'data',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['file', 'string', 'object'],
    providedOutputs: ['success', 'string'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'pdf_generator': {
    nodeType: 'pdf_generator',
    role: 'data',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['object', 'string'],
    providedOutputs: ['file'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'excel_processor': {
    nodeType: 'excel_processor',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['file', 'array'],
    providedOutputs: ['array', 'file'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'csv_parser': {
    nodeType: 'csv_parser',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'file'],
    providedOutputs: ['array', 'object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // ==========================================
  // DATABASE NODE-OK
  // ==========================================
  
  'database_query': {
    nodeType: 'database_query',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['array', 'object'], // Több sor vagy 1 sor
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'database_insert': {
    nodeType: 'database_insert',
    role: 'data',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['object', 'array'],
    providedOutputs: ['success', 'object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'database_update': {
    nodeType: 'database_update',
    role: 'data',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['object'],
    providedOutputs: ['success', 'object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'database_delete': {
    nodeType: 'database_delete',
    role: 'data',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['success'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // ==========================================
  // MATH & STRING NODE-OK
  // ==========================================
  
  'math_operation': {
    nodeType: 'math_operation',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['number', 'string'],
    providedOutputs: ['number'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'date_time': {
    nodeType: 'date_time',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'number', 'object'],
    providedOutputs: ['string', 'object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'random_generator': {
    nodeType: 'random_generator',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['number', 'string'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // ==========================================
  // ERROR HANDLING NODE-OK
  // ==========================================
  
  'error_logger': {
    nodeType: 'error_logger',
    role: 'terminal',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['error', 'any'],
    providedOutputs: ['error'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // Hiányzó node típusok - alapértelmezett szabályokkal
  'action': {
    nodeType: 'action',
    role: 'action',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['success', 'any'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'form': {
    nodeType: 'form',
    role: 'action',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'approval': {
    nodeType: 'approval',
    role: 'action',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['boolean', 'object'],
    maxInputConnections: 1,
    maxOutputConnections: 2,
    canConnectToSelf: false,
    requiresMultipleOutputs: true
  },
  
  'integration': {
    nodeType: 'integration',
    role: 'integration',
    canBeFirst: false,
    canBeLast: true,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['any'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // AI node-ok (hiányzók)
  'deepseek_chat': {
    nodeType: 'deepseek_chat',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['string', 'object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'gemini_chat': {
    nodeType: 'gemini_chat',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'object'],
    providedOutputs: ['string', 'object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'ai_image_generator': {
    nodeType: 'ai_image_generator',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string'],
    providedOutputs: ['file', 'string'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'ai_text_analyzer': {
    nodeType: 'ai_text_analyzer',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string'],
    providedOutputs: ['object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'ai_summarizer': {
    nodeType: 'ai_summarizer',
    role: 'integration',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string'],
    providedOutputs: ['string'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // File processing (hiányzók)
  'image_processor': {
    nodeType: 'image_processor',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['file', 'string'],
    providedOutputs: ['file'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // Data processing (hiányzók)
  'variable_set': {
    nodeType: 'variable_set',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['any'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'variable_get': {
    nodeType: 'variable_get',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['any'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'text_processor': {
    nodeType: 'text_processor',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string'],
    providedOutputs: ['string'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'counter': {
    nodeType: 'counter',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['number'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'multi_condition': {
    nodeType: 'multi_condition',
    role: 'logic',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['boolean', 'any'],
    maxInputConnections: 1,
    maxOutputConnections: 2,
    canConnectToSelf: false,
    requiresMultipleOutputs: true
  },
  
  'data_filter': {
    nodeType: 'data_filter',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['array', 'object'],
    providedOutputs: ['array'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'data_sort': {
    nodeType: 'data_sort',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['array'],
    providedOutputs: ['array'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'string_formatter': {
    nodeType: 'string_formatter',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'any'],
    providedOutputs: ['string'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'list_processor': {
    nodeType: 'list_processor',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['array'],
    providedOutputs: ['array', 'any'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // Parser node-ok (hiányzók)
  'xml_parser': {
    nodeType: 'xml_parser',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'file'],
    providedOutputs: ['object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  'yaml_parser': {
    nodeType: 'yaml_parser',
    role: 'data',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['string', 'file'],
    providedOutputs: ['object'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: false,
    requiresMultipleOutputs: false
  },
  
  // Error handling (hiányzók)
  'try_catch': {
    nodeType: 'try_catch',
    role: 'logic',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['success', 'error'],
    maxInputConnections: 1,
    maxOutputConnections: 2,
    canConnectToSelf: false,
    requiresMultipleOutputs: true
  },
  
  'retry': {
    nodeType: 'retry',
    role: 'logic',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['any'],
    maxInputConnections: 1,
    maxOutputConnections: 1,
    canConnectToSelf: true,
    requiresMultipleOutputs: false
  },
  
  'timeout': {
    nodeType: 'timeout',
    role: 'logic',
    canBeFirst: false,
    canBeLast: false,
    requiresPrevious: true,
    acceptedInputs: ['any'],
    providedOutputs: ['success', 'error'],
    maxInputConnections: 1,
    maxOutputConnections: 2,
    canConnectToSelf: false,
    requiresMultipleOutputs: true
  }
};
